import { useEffect, useMemo } from "react";
import { useQueryClient } from "react-query";
import { z } from "zod";
import { Controller } from "react-hook-form";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import { useMeQuery, useMyBuildingTreeQuery } from "../../../hooks/me/useMe";
import FormLayout from "../../Layout/FormLayout";
import Layout from "../../templates/Layout";
import { ulidSchema } from "../../../schemas/commonSchema";
import { useFormWithSchema } from "../../../hooks/useFormWithSchema";
import { flattenBuildings } from "../../../utils/building";
import { isSystemAdmin, isWorker } from "../../../utils/role/isRole";
import LoadingButton from "../../Button/LoadingButton";
import { useAppNavigate } from "../../../routes/useAppNavigate";
import { handleError } from "../../../utils/error/handleError";
import { callMySetCurrentBuilding } from "../../../api/me";
import { notify } from "../../../lib/notify";
import { meKey } from "../../../hooks/me/meKeys";
import { TenantWithBuildingTreeList } from "../../../types/response/tenant";
import { workerKey } from "hooks/worker/keys";

const DefaultOptionValue = "DEFAULT";

const schema = z.object({
  tenantId: ulidSchema.or(z.literal(DefaultOptionValue)),
  buildingId: ulidSchema,
});

const findTenantIdByBuildingId = (
  data: TenantWithBuildingTreeList,
  buildingId: string
): string | undefined => {
  if (data.tenants.length === 0) return undefined;
  for (const tenant of data.tenants) {
    if (tenant.markets.length === 0) continue;
    for (const market of tenant.markets) {
      if (market.areas.length === 0) continue;
      for (const area of market.areas) {
        if (area.buildings.length === 0) continue;
        const found = area.buildings.find((b) => b.buildingId === buildingId);
        if (found) {
          return tenant.tenantId;
        }
      }
    }
  }

  return undefined;
};

type FormInput = z.infer<typeof schema>;

const SetCurrentBuildingPage = () => {
  const { backNavigate, meNavigate } = useAppNavigate();
  const { data: loginUser, isFetching } = useMeQuery();
  const { data: optionData } = useMyBuildingTreeQuery();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (loginUser && isWorker(loginUser.role)) {
      meNavigate.detail();
    }
  }, [loginUser, meNavigate]);

  const onSubmit = async (input: FormInput) => {
    try {
      await callMySetCurrentBuilding({ buildingId: input.buildingId });

      notify("更新しました");
      queryClient.invalidateQueries(meKey.all);
      queryClient.invalidateQueries(workerKey.all);
      meNavigate.detail();
    } catch (error) {
      handleError(error);
    }
  };

  const {
    formState: { isSubmitting, errors },
    onSubmitHandler,
    control,
    watch,
    setValue,
  } = useFormWithSchema(schema, onSubmit, {
    tenantId: DefaultOptionValue,
    buildingId: DefaultOptionValue,
  });

  useEffect(() => {
    if (!loginUser || !optionData) return;

    if (loginUser.tenant) {
      setValue("tenantId", loginUser.tenant?.tenantId);
    }

    if (loginUser.currentBuilding) {
      if (isSystemAdmin(loginUser.role)) {
        // システム管理者の場合、テナントは空なので optionDataから検索してセットする
        const tenantId = findTenantIdByBuildingId(
          optionData,
          loginUser.currentBuilding.buildingId
        );
        if (!tenantId) return;
        setValue("tenantId", tenantId);
      }
      setValue("buildingId", loginUser.currentBuilding.buildingId);
    }
  }, [loginUser, setValue, optionData]);

  const currentTenantId = watch("tenantId");
  const buildingOptions = useMemo(() => {
    const tenant = optionData?.tenants.find(
      (t) => t.tenantId === currentTenantId
    );
    return flattenBuildings(tenant);
  }, [optionData, currentTenantId]);
  const isBuildingOptionEmpty = buildingOptions.length === 0;

  return (
    <Layout title="利用ビル変更">
      <FormLayout toolbar={<Typography>利用ビル変更</Typography>}>
        <Box component={"form"} noValidate onSubmit={onSubmitHandler}>
          <Stack spacing={2}>
            {isSystemAdmin(loginUser?.role) && (
              <FormControl fullWidth error={!!errors.tenantId} margin="normal">
                <InputLabel size="small" id="tenant-label">
                  テナント
                </InputLabel>
                <Controller
                  name="tenantId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      labelId="tenant-label"
                      label="テナント"
                      {...field}
                      size="small"
                      disabled={isSubmitting}
                      onChange={(e) => {
                        setValue("buildingId", DefaultOptionValue);
                        field.onChange(e);
                      }}
                    >
                      <MenuItem value={DefaultOptionValue}>
                        テナントを選択してください
                      </MenuItem>
                      {optionData?.tenants.map((t) => (
                        <MenuItem key={t.tenantId} value={t.tenantId}>
                          {t.tenantName}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.tenantId?.message}</FormHelperText>
              </FormControl>
            )}
            <FormControl fullWidth error={!!errors.buildingId} margin="normal">
              <InputLabel size="small" id="building-label">
                利用ビル
              </InputLabel>
              <Controller
                name="buildingId"
                control={control}
                render={({ field }) => (
                  <Select
                    labelId="building-label"
                    label="利用ビル"
                    {...field}
                    size="small"
                    disabled={isSubmitting || isBuildingOptionEmpty}
                  >
                    <MenuItem value={DefaultOptionValue}>
                      {isBuildingOptionEmpty
                        ? "管理範囲内にビルがありません"
                        : "利用ビルを選択してください"}
                    </MenuItem>
                    {buildingOptions.map((b) => (
                      <MenuItem key={b.buildingId} value={b.buildingId}>
                        {b.pathName}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText>{errors.buildingId?.message}</FormHelperText>
            </FormControl>
          </Stack>
          <Stack
            direction={"row"}
            justifyContent={"flex-end"}
            sx={{ py: 4 }}
            spacing={2}
          >
            <Button
              onClick={() => backNavigate()}
              variant="outlined"
              sx={{ minWidth: "120px" }}
              disabled={isSubmitting}
            >
              キャンセル
            </Button>
            <LoadingButton
              isLoading={isSubmitting}
              type="submit"
              variant="contained"
              sx={{ minWidth: "120px" }}
              disabled={isFetching}
            >
              保存
            </LoadingButton>
          </Stack>
        </Box>
      </FormLayout>
    </Layout>
  );
};

export default SetCurrentBuildingPage;
